import { isEmpty } from 'lodash-es';
import { paramCleaner } from '@/helpers/formatters';
import { getRequest, postRequest } from './request';

export const initSettings = async () => {
  const data = await getRequest({ url: '/settings-api/init' });
  return {
    projectName: data.projectName,
    atsName: data.atsName,
    atsId: data.atsId,
    integrationTypeKey: data.integrationTypeKey,
    projectImageUrl: data.projectImageUrl,
    showIntegrationsPage: data.showIntegrationsPage,
    canTeam: data.canTeam,
    canTeamAdmin: data.canTeamAdmin,
    canIntegrations: data.canIntegrations,
    canEditTemplates: data.canEditTemplates,
    canSeeTemplates: data.canSeeTemplates,
    canBilling: data.canBilling,
    canGreenhouseRoles: data.canGreenhouseRoles,
    canCustomFields: data.canCustomFields,
  };
};

// Account
// =============================================

export const getAccountSettings = async () => {
  const data = await getRequest({ url: '/settings-api/account' });

  if (!data.attributes) {
    throw new Error('No attributes');
  }

  const { firstName, lastName, email } = data.attributes;
  return { firstName, lastName, email };
};

export const updatePersona = async (personaKey, userId) => {
  let url = '/settings-api/persona';

  if (userId) {
    url += `?userId=${userId}`;
  }

  const response = await postRequest({
    url,
    body: { personaKey },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Update failed');
};

export const updateAccountSettings = async ({ firstName, lastName }) => {
  const response = await postRequest({
    url: '/settings-api/account',
    body: { firstName, lastName },
  });

  const {
    attributes, errors, code, message, success,
  } = response;

  if (success) {
    return { success, attributes, code };
  }

  if (code === 'validation_failed') {
    return { attributes, errors, code };
  }

  throw new Error(message || code || 'Update failed');
};

// Notifications
// =============================================

export const getNotificationsSettings = async () => {
  const data = await getRequest({ url: '/settings-api/notifications' });

  return {
    notificationFields: data,
  };
};

export const updateNotificationsSettings = async ({ id, action }) => {
  const response = await postRequest({
    url: '/settings-api/notification-toggle',
    body: { id, action },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Update failed');
};

// Integrations
// =============================================

export const getIntegrationsSettings = async () => {
  const data = await getRequest({ url: '/settings-api/integrations' });
  return {
    data,
  };
};

export const submitIntegrationsInfo = async (integrationsInfo) => {
  const response = await postRequest({
    url: '/settings-api/integrations',
    body: integrationsInfo,
  });

  const {
    attributes, code, errors, message, success,
  } = response;

  if (success) {
    return { success, code };
  }

  if (code === 'validation_failed') {
    return {
      attributes, code, errors, message,
    };
  }

  throw new Error(message || code || 'Information could not be submitted');
};

// Teams
// =============================================

export const getTeamSettings = async ({ selectedFilters, params = {} }) => {
  const {
    sort, page, searchTerm, to, from, ...paramFilters
  } = params;

  const cleanedParams = paramCleaner(paramFilters);

  const searchParams = new URLSearchParams();
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);
  if (typeof page !== 'undefined') searchParams.append('page', page);

  const {
    models: teamList,
    filters,
    textFilter,
    pagination,
    sorting,
    totals,
  } = await postRequest({
    url: `/settings-api/team?${searchParams}`,
    body: {
      textFilter: searchTerm,
      filters: !isEmpty(selectedFilters) ? selectedFilters : cleanedParams,
    },
  });

  return {
    teamList, filters, textFilter, pagination, sorting, totals,
  };
};

export const getAdministrators = async () => getRequest({
  url: '/settings-api/administrators',
});

export const getDemographicSurveyAdmins = async () => getRequest({
  url: '/settings-api/demographic-survey-admins',
});

export const getDemographicSurveyUsers = async () => getRequest({
  url: '/settings-api/demographic-survey-users',
});

export const removeAccessToDemographicSurveyReport = async (userId) => {
  const response = await postRequest({
    url: '/settings-api/demographic-survey-remove-access',
    body: { userId },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Removing access failed');
};

export const grantAccessToDemographicSurveyReport = async (userIds) => {
  const response = await postRequest({
    url: '/settings-api/demographic-survey-grant-access',
    body: { userIds },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Granting access failed');
};

export const downloadTeamCSV = async ({ selectedFilters, params = {} }) => {
  const {
    sort, searchTerm,
  } = params;

  const searchParams = new URLSearchParams();
  if (typeof sort !== 'undefined') searchParams.append('sort', sort);

  return postRequest({
    url: `/settings-api/team-download?${searchParams}`,
    body: {
      textFilter: searchTerm,
      filters: selectedFilters,
    },
    isCsv: true,
  });
};

export const updateTeamSettings = async ({ id, action }) => {
  const response = await postRequest({
    url: '/settings-api/team-admin-toggle',
    body: { id, action },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Update failed');
};

export const deleteTeamSettings = async ({ id }) => {
  const response = await postRequest({
    url: '/settings-api/team-remove',
    body: { id },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Delete failed');
};

export const sendTeamInviteEmail = async (email) => {
  const response = await postRequest({
    url: '/settings-api/team-invite',
    body: { email },
  });

  const {
    attributes, errors, code, message, success,
  } = response;

  if (success) {
    return { success, attributes, code };
  }

  if (code === 'validation_failed') {
    return { attributes, errors, code };
  }

  throw new Error(message || code || 'Invite failed');
};

export const sendTeamDatapeopleAnywhereEmail = async (userId) => {
  const response = await postRequest({
    url: '/settings-api/team-datapeople-anywhere-invite',
    body: { userId },
  });

  const {
    attributes, errors, code, message, success,
  } = response;

  if (success) {
    return { success, attributes, code };
  }

  if (code === 'validation_failed') {
    return { attributes, errors, code };
  }

  throw new Error(message || code || 'Invite failed');
};

export const sendTeamOnboardEmail = async (userIds) => {
  const response = await postRequest({
    url: '/settings-api/team-onboard',
    body: { userIds },
  });

  const {
    attributes, errors, code, message, success,
  } = response;

  if (success) {
    return { success, attributes, code };
  }

  if (code === 'validation_failed') {
    return { attributes, errors, code };
  }

  throw new Error(message || code || 'Onboard failed');
};

// User Roles
// =============================================

export const getUserRolesSettings = async () => {
  const data = await getRequest({ url: '/settings-api/greenhouse-roles' });

  return {
    roles: data.map((item) => ({
      id: item.id,
      name: item.name,
      type: item.type,
      allowJobAccess: item.allow_job_access,
    })),
  };
};

export const updateUserRolesSettings = async ({ id, action }) => {
  const response = await postRequest({
    url: '/settings-api/greenhouse-role-toggle',
    body: { id, action },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Update failed');
};

// Custom Fields
// =============================================

export const getCustomFieldsSettings = async () => {
  const data = await getRequest({ url: '/settings-api/custom-fields' });

  return {
    customFields: data.map((item) => ({
      id: item.id,
      name: item.name,
      isPrivate: item.is_private,
      isSearch: item.is_search,
    })),
  };
};

export const updateCustomFieldsSettings = async ({ id, action }) => {
  const response = await postRequest({
    url: '/settings-api/custom-field-toggle',
    body: { id, action },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Update failed');
};

// Acknowledgements (bearsign config)
// =============================================
const ACKNOWLEDGEMENTS_CONFIG_KEYS = [
  'company_name',
  'acknowledgement_email_intro',
  'acknowledgement_agreement_text',
  'acknowledgement_pdf_text',
  'hr_contact_email',
  'acknowledgement_cycle_sla',
  'acknowledgement_pending_sla',
];

const ACKNOWLEDGEMENTS_CONFIG_CATEGORY = 'acknowledgement';

const ACKNOWLEDGEMENTS_CONFIG_SLA_KEYS = [
  'acknowledgement_cycle_sla',
  'acknowledgement_pending_sla',
];

export const normalizeAcknowledgementsConfig = (data) => ACKNOWLEDGEMENTS_CONFIG_KEYS.reduce((acc, key) => {
  acc[key] = String(data?.[key]?.value ?? '');
  return acc;
}, {});

export const serializeAcknowledgementsConfigRequest = (flatValues) => (
  ACKNOWLEDGEMENTS_CONFIG_KEYS.reduce((acc, key) => {
    let value;

    if (ACKNOWLEDGEMENTS_CONFIG_SLA_KEYS.includes(key)) {
      const slaValue = Number(flatValues?.[key]);

      value = Number.isFinite(slaValue) ? slaValue : 0;
    } else {
      value = String(flatValues?.[key] || '');
    }

    acc[key] = {
      category: ACKNOWLEDGEMENTS_CONFIG_CATEGORY,
      value,
    };

    return acc;
  }, {})
);

export const getAcknowledgementsConfig = async () => {
  const data = await getRequest({ url: '/settings-api/acknowledgements-config' });

  return normalizeAcknowledgementsConfig(data);
};

export const updateAcknowledgementsConfig = async (body) => {
  const response = await postRequest({
    url: '/settings-api/acknowledgements-config',
    body: serializeAcknowledgementsConfigRequest(body),
  });

  if (response.success && response.attributes) {
    return { success: true, attributes: normalizeAcknowledgementsConfig(response.attributes) };
  }

  throw new Error('Acknowledgements config update failed');
};

// Project feature flags (AI, Job Posts)
// =============================================
export const getProjectEditableFeatureFlags = async () => getRequest({ url: '/settings-api/project-feature-flags' });

export const updateProjectEditableFeatureFlags = async (body) => {
  const response = await postRequest({
    url: '/settings-api/project-feature-flags',
    body,
  });

  if (response.success && response.attributes) {
    return { success: true, attributes: response.attributes };
  }

  throw new Error('Project feature flags update failed');
};

// Style Guide
// =============================================

export const getStyleGuide = async () => {
  const styleGuides = await getRequest({ url: '/settings-api/project-tags' });
  return styleGuides;
};

export const updateStyleGuide = async ({ id, action }) => {
  const response = await postRequest({
    url: '/settings-api/project-tag-toggle',
    body: { id, action },
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'Update failed');
};

// SSO Setup
// ==============================================

export const getSsoSetup = async () => getRequest({ url: '/settings-api/sso-setup' });

export const postSsoSetup = async (body) => {
  const response = await postRequest({
    url: '/settings-api/sso-setup',
    body,
  });

  const { code, success, message } = response;

  if (success) {
    return { success, code };
  }

  throw new Error(message || code || 'SSO update failed');
};

export const getAllTemplates = async () => {
  const { templates } = await getRequest({ url: '/settings-api/templates' });
  return { templates };
};
